/**
 * Canonical Field Types and Renderer Helper - Phase 5B.2
 *
 * UI-side types matching backend CanonicalPropertyDTO contract.
 * Provides formatField() helper for consistent rendering of known/unknown fields.
 *
 * Lock Header:
 * - Owner: Dana Patterson
 * - Effective: 2026-02-05
 * - Version: v1.0.0
 */

// ============================================================================
// Field Value Types (mirrors backend canonicalDTO.ts)
// ============================================================================

export interface KnownFieldValue<T> {
    state: 'known';
    value: T;
    source: string;
    confidence: number;
    last_verified_at: string;
}

export interface UnknownFieldValue {
    state: 'unknown';
    reason: UnknownReason;
    source?: string;
    confidence?: number;
    last_verified_at?: string;
}

export type FieldValue<T> = KnownFieldValue<T> | UnknownFieldValue;

export type UnknownReason =
    | 'source_did_not_publish'
    | 'not_applicable'
    | 'redacted'
    | 'normalization_failed'
    | 'geocoding_failed'
    | 'classification_failed'
    | 'computation_pending'
    | 'enrichment_pending'
    | 'hitl_required';

// ============================================================================
// Unknown Reason to UI Label Mapping
// ============================================================================

const UNKNOWN_REASON_LABELS: Record<UnknownReason, string> = {
    source_did_not_publish: 'Not published',
    not_applicable: 'N/A',
    redacted: 'Redacted',
    normalization_failed: 'Data error',
    geocoding_failed: 'Location unavailable',
    classification_failed: 'Classification error',
    computation_pending: 'Computing...',
    enrichment_pending: 'Loading...',
    hitl_required: 'Review required',
};

// ============================================================================
// Canonical Property DTO (mirrors backend)
// ============================================================================

export interface CanonicalLocation {
    lat: number;
    lon: number;
    confidence: number;
}

export interface CanonicalFields {
    beds: FieldValue<number>;
    baths: FieldValue<number>;
    living_sqft: FieldValue<number>;
    year_built: FieldValue<number>;
    acreage: FieldValue<number>;
    condition: FieldValue<string>;
    property_type: FieldValue<string>;
    assessed_value: FieldValue<number>;
    market_value: FieldValue<number>;
    owner_name: FieldValue<string>;
    address_raw: FieldValue<string>;
}

export interface CanonicalSummary {
    display_address: string;
    display_owner: string;
}

export interface CanonicalPropertyDTO {
    propertyId: string;
    jurisdiction: string;
    location: CanonicalLocation;
    canonical: CanonicalFields;
    summary: CanonicalSummary;
}

// ============================================================================
// Renderer Helper
// ============================================================================

export interface FormattedField {
    displayValue: string;
    isKnown: boolean;
    tooltip: string;
}

/**
 * Format a FieldValue for display in the UI.
 *
 * @param field - The FieldValue to format
 * @param formatter - Optional formatter function for known values (e.g., formatCurrency)
 * @returns FormattedField with displayValue, isKnown flag, and tooltip
 */
export function formatField<T>(
    field: FieldValue<T> | undefined | null,
    formatter?: (value: T) => string
): FormattedField {
    // Handle undefined/null (legacy data without canonical fields)
    if (!field) {
        return {
            displayValue: 'Not published',
            isKnown: false,
            tooltip: 'No data available',
        };
    }

    if (field.state === 'known') {
        const displayValue = formatter ? formatter(field.value) : String(field.value);
        const tooltip = buildKnownTooltip(field);
        return {
            displayValue,
            isKnown: true,
            tooltip,
        };
    }

    // Unknown field
    const displayValue = UNKNOWN_REASON_LABELS[field.reason] || 'Unknown';
    const tooltip = buildUnknownTooltip(field);
    return {
        displayValue,
        isKnown: false,
        tooltip,
    };
}

/**
 * Build tooltip text for a known field value
 */
function buildKnownTooltip<T>(field: KnownFieldValue<T>): string {
    const parts: string[] = [];

    if (field.source) {
        parts.push(`Source: ${field.source}`);
    }

    if (field.confidence !== undefined) {
        const confidencePercent = Math.round(field.confidence * 100);
        parts.push(`Confidence: ${confidencePercent}%`);
    }

    if (field.last_verified_at) {
        const date = new Date(field.last_verified_at);
        parts.push(`Verified: ${date.toLocaleDateString()}`);
    }

    return parts.join(' | ') || 'Data available';
}

/**
 * Build tooltip text for an unknown field value
 */
function buildUnknownTooltip(field: UnknownFieldValue): string {
    const parts: string[] = [];

    const reasonLabel = UNKNOWN_REASON_LABELS[field.reason] || field.reason;
    parts.push(`Reason: ${reasonLabel}`);

    if (field.source) {
        parts.push(`Source: ${field.source}`);
    }

    if (field.last_verified_at) {
        const date = new Date(field.last_verified_at);
        parts.push(`Checked: ${date.toLocaleDateString()}`);
    }

    return parts.join(' | ');
}

// ============================================================================
// Type Guards
// ============================================================================

export function isKnown<T>(field: FieldValue<T> | undefined | null): field is KnownFieldValue<T> {
    return field !== undefined && field !== null && field.state === 'known';
}

export function isUnknown<T>(field: FieldValue<T> | undefined | null): field is UnknownFieldValue {
    return field !== undefined && field !== null && field.state === 'unknown';
}

// ============================================================================
// Value Extractors
// ============================================================================

/**
 * Extract the raw value from a FieldValue, returning undefined if unknown
 */
export function extractValue<T>(field: FieldValue<T> | undefined | null): T | undefined {
    if (isKnown(field)) {
        return field.value;
    }
    return undefined;
}

/**
 * Extract the raw value with a default fallback
 */
export function extractValueOr<T>(field: FieldValue<T> | undefined | null, defaultValue: T): T {
    if (isKnown(field)) {
        return field.value;
    }
    return defaultValue;
}

// ============================================================================
// Formatters for Common Field Types
// ============================================================================

export function formatCurrency(value: number): string {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(value);
}

export function formatNumber(value: number): string {
    return new Intl.NumberFormat('en-US').format(value);
}

export function formatSqft(value: number): string {
    return `${formatNumber(value)} sqft`;
}

export function formatAcreage(value: number): string {
    return `${value.toFixed(2)} acres`;
}

export function formatBaths(value: number): string {
    // Handle half baths (e.g., 2.5 -> "2.5")
    if (value % 1 !== 0) {
        return value.toFixed(1);
    }
    return String(value);
}

// ============================================================================
// Backward Compatibility Helpers
// ============================================================================

/**
 * Convert a legacy raw value to a FieldValue for backward compatibility.
 * Use this when consuming data from endpoints that haven't been updated yet.
 */
export function toLegacyFieldValue<T>(
    value: T | null | undefined,
    source: string = 'legacy'
): FieldValue<T> {
    if (value === null || value === undefined) {
        return {
            state: 'unknown',
            reason: 'source_did_not_publish',
            source,
        };
    }
    return {
        state: 'known',
        value,
        source,
        confidence: 1.0,
        last_verified_at: new Date().toISOString(),
    };
}

/**
 * Check if a property has canonical fields (vs legacy format)
 */
export function hasCanonicalFields(property: unknown): property is { canonical: CanonicalFields } {
    return (
        typeof property === 'object' &&
        property !== null &&
        'canonical' in property &&
        typeof (property as { canonical: unknown }).canonical === 'object'
    );
}
